/**
 * Речь со своего сервера: озвучка (Piper — голос на каждый язык, Kokoro —
 * английский) и распознавание (faster-whisper) там, где браузер его не
 * умеет (Firefox, часть Safari, WebView). Голоса браузеров звучат везде
 * по-разному, а серверный — одинаково на любом устройстве.
 *
 * Озвучка включена для языков, которые сервер назвал в `/api/speech/status`
 * (`ttsLanguages`). Иначе и при любом сбое `speech.ts` читает фразу голосом
 * браузера; после сбоя сервер минуту не спрашивается, чтобы каждая фраза
 * не ждала недоступный сервис.
 *
 * Распознавание: запись микрофона (MediaRecorder) с простым детектором
 * голоса — громкость выше порога начинает фразу, пауза её заканчивает.
 * Запись уходит на сервер целиком, расшифровка приходит одной строкой.
 */

import type { LanguageCode } from '@aptile/shared';

import { apiFetch, apiRequest } from '../api/client';
import { ensurePlaybackSession, prepareCaptureSession } from '../lib/audioSession';
import { isIOS } from '../lib/platform';
import { acquireMic, type MicLease } from './micStream';

interface SpeechStatus {
  readonly stt: boolean;
  /** Потоковое распознавание Deepgram на сервере (старый сервер не присылает). */
  readonly sttStream?: boolean;
  readonly tts: boolean;
  /** Языки, которые озвучивает сервер (старый сервер не присылает — значит, английский). */
  readonly ttsLanguages?: readonly LanguageCode[];
}

let status: SpeechStatus = { stt: false, tts: false };
let loading: Promise<void> | null = null;
let loadedAt = 0;
/** Сервер докачивает голоса после старта — список языков обновляется раз в 5 минут. */
const STATUS_TTL_MS = 5 * 60 * 1000;
/**
 * Сколько ждать звук с сервера, прежде чем читать голосом браузера.
 * Фраза синтезируется за доли секунды (и часто уже лежит в кэше): если
 * сервер не ответил за четыре, ждать дольше дороже, чем сразу озвучить
 * браузером — иначе «Тико говорит» просто висит.
 */
const SPEAK_TIMEOUT_MS = 4000;
/** После сбоя сервер какое-то время не спрашивается: фразы сразу идут браузеру. */
const BACKOFF_MS = 60_000;
let serverDownUntil = 0;
/**
 * Запись микрофона не завелась — минуту её не начинаем: фразу на это время
 * берёт браузерное распознавание, если оно есть. Сбой отправки или ответ
 * 5xx такой паузы не заслуживает: следующий тап снова должен попробовать
 * сервер, иначе в России (где Web Speech не работает) ученик остаётся без
 * распознавания до перезагрузки.
 */
const STT_BACKOFF_MS = 60_000;
let sttDownUntil = 0;
/**
 * Расшифровка одной фразы. Whisper на короткой записи отвечает за
 * секунды; четверть минуты — потолок, после которого «…» на экране
 * висит уже неприлично долго.
 */
const TRANSCRIBE_TIMEOUT_MS = 25_000;
/** Пауза перед повтором: аудиосессия записи на iOS успевает закрыться. */
const RETRY_DELAY_MS = 200;

/**
 * Сигнал-срок запроса. `AbortSignal.timeout` появился только в Safari 16.4:
 * на старом iOS вызов бросил бы `TypeError` прямо в обработчике остановки
 * записи, и расшифровка не ушла бы вовсе. Свой таймер работает везде.
 */
function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  window.setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/** Узнать у сервера, есть ли своя речь. Вызывается после входа; повторно — без запроса. */
export function initServerSpeech(): Promise<void> {
  loading ??= apiRequest<SpeechStatus>('/api/speech/status')
    .then((value) => {
      status = value;
      loadedAt = Date.now();
    })
    .catch(() => {
      // Нет сети или старый сервер — остаёмся на браузерной речи. Попытка
      // считается: после устаревания статуса (5 минут) спросим снова.
      loading = null;
      loadedAt = Date.now();
    });
  return loading;
}

/** Статус устарел — обновляем в фоне: сервер мог включить речь без перезагрузки страницы. */
function refreshIfStale(): void {
  if (loadedAt > 0 && Date.now() - loadedAt > STATUS_TTL_MS) {
    loading = null;
    void initServerSpeech();
  }
}

/** Озвучивает ли сервер этот язык сейчас (и не упал ли он только что). */
export function serverTtsSpeaks(language: LanguageCode): boolean {
  refreshIfStale();
  if (!status.tts || Date.now() < serverDownUntil) return false;
  return (status.ttsLanguages ?? ['en']).includes(language);
}

function recordingSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && navigator.mediaDevices?.getUserMedia !== undefined;
}

/** Разрешение на микрофон: за сессию вкладки спрашивается один раз. */
let micAccess: 'unknown' | 'granted' | 'denied' = 'unknown';
let micPending: Promise<boolean> | null = null;

/**
 * Спросить разрешение на микрофон один раз — в начале работы с приложением.
 * Дальше запись берёт уже выданное разрешение, и окно не появляется снова.
 *
 * Спрашивать нужно по касанию: iOS показывает запрос только в обработчике
 * жеста, при загрузке страницы `getUserMedia` сразу отказывает. Поэтому
 * `getUserMedia` зовётся сразу, без ожиданий; если разрешение уже выдано,
 * браузер отдаёт поток без окна. Поток тут же отпускается — он нужен был
 * только ради разрешения.
 */
/** Итог проверки разрешения (`micPermission.ts`): запись и перебивание берут его без нового запроса. */
export function markMicrophoneAccess(state: 'granted' | 'denied'): void {
  micAccess = state;
}

export function requestMicrophoneAccess(): Promise<boolean> {
  if (micAccess !== 'unknown') return Promise.resolve(micAccess === 'granted');
  if (!recordingSupported()) return Promise.resolve(false);
  prepareCaptureSession();
  micPending ??= navigator.mediaDevices
    .getUserMedia({ audio: true })
    .then(
      (stream) => {
        stream.getTracks().forEach((track) => track.stop());
        micAccess = 'granted';
        return true;
      },
      (error: unknown) => {
        // Явный отказ запоминаем — второй раз окно не показываем. Остальные
        // сбои (нет устройства, микрофон занят) разрешением не считаются:
        // следующий тап по кнопке записи попробует ещё раз.
        if (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'SecurityError')) micAccess = 'denied';
        return false;
      },
    )
    .finally(() => {
      micPending = null;
    });
  return micPending;
}

/** Есть ли на сервере потоковое распознавание (Deepgram). */
export function serverStreamSttConfigured(): boolean {
  refreshIfStale();
  return status.sttStream === true;
}

/** Общий аудиоконтекст (разбужен жестом) — для потоковой записи. */
export function captureAudioContext(): AudioContext | null {
  return sharedAudioContext();
}

/** Свой сервер распознавания настроен и браузер умеет записывать — без учёта паузы после сбоя. */
export function serverRecordingConfigured(): boolean {
  refreshIfStale();
  return status.stt && recordingSupported();
}

/** Есть ли свой сервер распознавания (faster-whisper) — запасной путь потока. */
export function localSttConfigured(): boolean {
  refreshIfStale();
  return status.stt;
}

/**
 * Экран, где сейчас говорит ученик (`call`, `drill`, `word-game`…). Уходит
 * с записью на свой сервер — метрики распознавания видны по экранам.
 * Слушает одновременно один экран, поэтому хватает одной переменной.
 */
let speechSourceTag = 'other';

export function setSpeechSource(source: string): void {
  speechSourceTag = source;
}

/** Распознать готовую запись на своём сервере (запасной путь потока); пустая строка — тишина. */
export async function transcribeOnServer(blob: Blob, type: string): Promise<string> {
  const deadline = timeoutSignal(TRANSCRIBE_TIMEOUT_MS);
  const send = async (): Promise<string> => {
    const response = await apiFetch('/api/speech/transcribe', { method: 'POST', body: blob, headers: { 'Content-Type': type, 'X-Speech-Source': speechSourceTag }, signal: deadline });
    if (!response.ok) throw new Error(`speech ${response.status}`);
    const payload = (await response.json()) as { readonly data?: { readonly text?: string } };
    return payload.data?.text?.trim() ?? '';
  };
  try {
    return await send();
  } catch (error: unknown) {
    if (deadline.aborted) throw error;
    await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS));
    return send();
  }
}

export function serverSttAvailable(): boolean {
  refreshIfStale();
  return status.stt && recordingSupported() && Date.now() >= sttDownUntil;
}

/* ── Озвучка ─────────────────────────────────────────────────────── */

/** Озвученные фразы в памяти вкладки: карточки и уроки повторяют одно и то же. */
const audioCache = new Map<string, string>();
/** Те же фразы, которые сейчас качаются: префетч и воспроизведение ждут один запрос, а не два. */
const audioPending = new Map<string, Promise<string>>();
const AUDIO_CACHE_SIZE = 120;
/**
 * Один плеер на всё приложение. Safari разрешает `play()` без жеста только
 * элементу, который однажды уже играл по жесту, — новый `Audio()` на каждое
 * предложение блокировался бы, и Тико посреди ответа переходил бы на голос
 * браузера. Поэтому элемент один, и при первом касании он «разблокируется»
 * беззвучным звуком.
 */
let player: HTMLAudioElement | null = null;
/** Завершить текущую фразу (конец, пауза системой, остановка, страховка). */
let finishCurrent: (() => void) | null = null;
/** Плеер уже играл по жесту — iOS пустит его без жеста и дальше. */
let playerUnlocked = false;

/** 0,05 с тишины (WAV 8 кГц): разблокирует плеер в Safari. */
const SILENCE =
  'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQQAAACAgICA';

/**
 * Общий `AudioContext` на всё приложение. На iOS контекст, созданный или
 * возобновлённый вне жеста, остаётся `suspended` и анализатор всегда
 * отдаёт нули. Раньше из-за этого детектор голоса считал ученика
 * молчащим, и запись уходила «вслепую» только через семь секунд — на
 * iPhone каждая фраза ждала целую вечность. Контекст создаётся лениво и
 * будится на каждом касании; закрывать его нельзя — только отключать
 * узлы текущей записи.
 */
interface AudioWindow {
  readonly AudioContext?: typeof AudioContext;
  readonly webkitAudioContext?: typeof AudioContext;
}

let audioContext: AudioContext | null = null;

function sharedAudioContext(): AudioContext | null {
  if (audioContext !== null) return audioContext;
  if (typeof window === 'undefined') return null;
  const audioWindow = window as unknown as AudioWindow;
  const Constructor = audioWindow.AudioContext ?? audioWindow.webkitAudioContext;
  if (Constructor === undefined) return null;
  try {
    audioContext = new Constructor();
    return audioContext;
  } catch {
    return null;
  }
}

/**
 * Пересоздать аудиоконтекст с нуля (кнопка «Восстановить»): старый
 * закрывается, новый создаётся и будится прямо в касании — iOS иначе
 * оставляет его `suspended`. Застрявший после прерывания контекст обычно
 * лечится только так.
 */
export function resetAudioContext(): void {
  const old = audioContext;
  audioContext = null;
  if (old !== null) void old.close().catch(() => undefined);
  const fresh = sharedAudioContext();
  if (fresh !== null) void fresh.resume().catch(() => undefined);
}

/** Состояние общего аудиоконтекста — для диагностики (`none` — ещё не создан). */
export function audioContextState(): string {
  return audioContext === null ? 'none' : (audioContext.state as string);
}

/** Снять паузу после сбоя записи (кнопка «Восстановить»): следующая фраза снова может уйти на сервер. */
export function resetServerSttBackoff(): void {
  sttDownUntil = 0;
}

/** Как часто во время звонка проверять, не уснул ли аудиоконтекст. */
const KEEP_ALIVE_CHECK_MS = 5000;

/**
 * Звонок: аудиоконтекст не должен засыпать. Через него идёт звук к Deepgram
 * и детектору голоса, а iOS переводит контекст в `suspended`/`interrupted`
 * (уведомление, Siri, блокировка) — после этого запись получает тишину и
 * Тико «не слышит». Поэтому на время звонка:
 *   неслышный источник на выходе (-100 дБ) — контекст с живым выходом
 *   система не усыпляет за простой;
 *   любое `statechange`, возвращение на экран, касание и проверка раз в
 *   5 с будят контекст обратно.
 * Возвращает «выключить».
 */
export function startAudioKeepAlive(): () => void {
  const context = sharedAudioContext();
  if (context === null) return () => undefined;
  let source: OscillatorNode | null = null;
  let gain: GainNode | null = null;
  try {
    source = context.createOscillator();
    source.frequency.value = 20;
    gain = context.createGain();
    gain.gain.value = 0.00001;
    source.connect(gain);
    gain.connect(context.destination);
    source.start();
  } catch {
    source = null;
  }
  const wake = (why: string) => {
    // `interrupted` — состояние только Safari, в типах его нет.
    const state = context.state as string;
    if (state === 'running' || state === 'closed') return;
    console.warn(`[mic] audio context ${state} — resuming (${why})`);
    void context.resume().catch(() => undefined);
  };
  const onState = () => wake('statechange');
  const onVisible = () => {
    if (document.visibilityState === 'visible') wake('visible');
  };
  const onTap = () => wake('tap');
  context.addEventListener('statechange', onState);
  document.addEventListener('visibilitychange', onVisible);
  document.addEventListener('pointerdown', onTap, true);
  const timer = window.setInterval(() => wake('keep-alive'), KEEP_ALIVE_CHECK_MS);
  return () => {
    window.clearInterval(timer);
    context.removeEventListener('statechange', onState);
    document.removeEventListener('visibilitychange', onVisible);
    document.removeEventListener('pointerdown', onTap, true);
    try {
      source?.stop();
    } catch {
      // уже остановлен
    }
    source?.disconnect();
    gain?.disconnect();
  };
}

/** Разбудить общий контекст — из жеста или перед записью. На iOS без этого анализатор молчит. */
export function resumeAudioContext(): void {
  const context = sharedAudioContext();
  // `interrupted` — состояние только Safari: iOS прерывает контекст, пока
  // играет `<audio>` (фраза дрилла). Будить нужно и его, а не только `suspended`.
  const state = context?.state as string | undefined;
  if (context !== null && state !== 'running' && state !== 'closed') void context.resume().catch(() => undefined);
}

/**
 * Аудиосессия Safari (голос Тико — `playback`, запись — `play-and-record`):
 * см. `lib/audioSession.ts`. Экспорт оставлен здесь для прежних импортов.
 */
export { ensurePlaybackSession };

function sharedPlayer(): HTMLAudioElement {
  if (player === null) {
    player = new Audio();
    player.preload = 'auto';
    player.setAttribute('playsinline', '');
  }
  return player;
}

/**
 * Разблокировать плеер настоящим жестом. Беззвучный WAV должен играть
 * БЕЗ `muted`: приглушённое воспроизведение iOS за жест не считает, и
 * первый настоящий `play()` после ожидания сети блокировался — серверная
 * озвучка на iPhone «не работала» вовсе.
 */
function unlockPlayer(): void {
  // Что-то уже играет — элемент и так разблокирован.
  if (playerUnlocked || finishCurrent !== null) {
    playerUnlocked = true;
    return;
  }
  const audio = sharedPlayer();
  audio.muted = false;
  audio.src = SILENCE;
  const played = audio.play();
  if (played === undefined) {
    playerUnlocked = true;
    return;
  }
  void played
    .then(() => {
      playerUnlocked = true;
    })
    .catch(() => undefined);
}

if (typeof document !== 'undefined') {
  // Слушатели не снимаем: после блокировки экрана iOS снова подвешивает
  // контекст, и следующее касание обязано его разбудить.
  const unlock = () => {
    resumeAudioContext();
    unlockPlayer();
  };
  document.addEventListener('pointerdown', unlock, true);
  document.addEventListener('touchend', unlock, true);
  document.addEventListener('keydown', unlock, true);
  document.addEventListener('visibilitychange', () => {
    // Экран заблокирован или вкладка скрыта — речь заканчивается сразу, а не
    // ждёт «ended», которого не будет: звонок не застревает на «Тико говорит».
    if (document.visibilityState === 'hidden') stopServerSpeech();
    else resumeAudioContext();
  });
}

/** Страховка: фраза не может звучать дольше своей длительности плюс запас. */
const PLAY_SLACK_MS = 2000;
/** Пока длительность неизвестна — оценка по длине текста. */
const PLAY_MS_PER_CHAR = 110;
const PLAY_BASE_MS = 4000;

export function stopServerSpeech(): void {
  if (finishCurrent === null) return;
  player?.pause();
  finishCurrent();
}

/** Скачать озвучку фразы и положить её в кэш вкладки. */
async function downloadAudio(key: string, text: string, speed: number, language: LanguageCode): Promise<string> {
  const response = await apiFetch('/api/tts/speak', {
    method: 'POST',
    body: JSON.stringify({ text, lang: language, speed }),
    signal: timeoutSignal(SPEAK_TIMEOUT_MS),
  }).catch((error: unknown) => {
    // Срок вышел — сервер мог просто задуматься: фразу прочтёт браузер,
    // но минуту серверной озвучки из-за одного опоздания не лишаемся.
    const name = typeof error === 'object' && error !== null && 'name' in error ? error.name : '';
    if (name !== 'AbortError') serverDownUntil = Date.now() + BACKOFF_MS;
    throw error;
  });
  if (!response.ok) {
    // Голоса для языка нет или сервис лёг — минуту не спрашиваем, читает браузер.
    if (response.status >= 500) serverDownUntil = Date.now() + BACKOFF_MS;
    throw new Error(`speech ${response.status}`);
  }
  const url = URL.createObjectURL(await response.blob());
  audioCache.set(key, url);
  if (audioCache.size > AUDIO_CACHE_SIZE) {
    const oldest = audioCache.keys().next();
    if (oldest.done !== true) {
      URL.revokeObjectURL(audioCache.get(oldest.value) ?? '');
      audioCache.delete(oldest.value);
    }
  }
  return url;
}

/**
 * Адрес озвучки фразы. Префетч и воспроизведение одного предложения
 * приходят сюда почти одновременно — они должны ждать один общий запрос,
 * иначе сервер синтезирует фразу дважды, а звук приходит вдвое позже.
 */
function audioUrl(text: string, speed: number, language: LanguageCode): Promise<string> {
  const key = `${language}|${speed}|${text}`;
  const cached = audioCache.get(key);
  if (cached !== undefined) return Promise.resolve(cached);
  const pending = audioPending.get(key);
  if (pending !== undefined) return pending;
  const request = downloadAudio(key, text, speed, language).finally(() => audioPending.delete(key));
  audioPending.set(key, request);
  return request;
}

/**
 * Подготовить озвучку заранее (следующее предложение, следующий дрилл,
 * частые фразы Тико): звук ляжет в кэш и проиграется без ожидания сервера.
 */
export function prefetchServerSpeech(text: string, rate: number, language: LanguageCode): void {
  if (!serverTtsSpeaks(language) || text.trim().length === 0) return;
  const speed = Math.min(1.5, Math.max(0.5, Math.round(rate * 20) / 20));
  void audioUrl(text, speed, language).catch(() => undefined);
}

/**
 * Проиграть фразу голосом сервера; промис — до конца речи (или до паузы
 * системой, блокировки экрана, остановки). Сбой — исключение (браузер подхватит).
 */
/** Раскодированный звук фраз (по адресу из `audioCache`): повтор фразы — без декодирования. */
const decodedCache = new Map<string, AudioBuffer>();
const DECODED_CACHE_SIZE = 40;

/**
 * Играть через Web Audio можно: на iOS — только с Audio Session API
 * (Safari 16.4+), иначе Web Audio молчит при выключенном звонке телефона,
 * а `<audio>` — нет.
 */
function webAudioPlaybackAllowed(): boolean {
  if (!isIOS()) return true;
  return typeof navigator !== 'undefined' && (navigator as unknown as { readonly audioSession?: unknown }).audioSession !== undefined;
}

function decodeAudio(context: AudioContext, data: ArrayBuffer): Promise<AudioBuffer | null> {
  // Старый Safari знает только форму с колбэками — она работает везде.
  return new Promise((resolve) => {
    try {
      const result = context.decodeAudioData(data, resolve, () => resolve(null)) as Promise<AudioBuffer> | undefined;
      result?.catch(() => resolve(null));
    } catch {
      resolve(null);
    }
  });
}

/**
 * Фраза через Web Audio — тот же аудиоконтекст, что у микрофона. `<audio>`
 * на iOS прерывает этот контекст, и микрофон дрилла сразу после фразы
 * получал тишину — распознавание уходило мимо Deepgram. Так звучит и голос
 * Тико в звонке. `false` — сыграть не вышло, играет `<audio>`.
 */
async function playWithWebAudio(url: string, cancelled: () => boolean): Promise<boolean> {
  if (!webAudioPlaybackAllowed()) return false;
  const context = sharedAudioContext();
  if (context === null) return false;
  if ((context.state as string) !== 'running') {
    await Promise.race([context.resume().catch(() => undefined), new Promise((resolve) => window.setTimeout(resolve, 300))]);
    if ((context.state as string) !== 'running') return false;
  }
  let buffer = decodedCache.get(url) ?? null;
  if (buffer === null) {
    try {
      buffer = await decodeAudio(context, await (await fetch(url)).arrayBuffer());
    } catch {
      buffer = null;
    }
    if (buffer === null) return false;
    decodedCache.set(url, buffer);
    if (decodedCache.size > DECODED_CACHE_SIZE) {
      const oldest = decodedCache.keys().next();
      if (oldest.done !== true) decodedCache.delete(oldest.value);
    }
  }
  if (cancelled()) return true;
  stopServerSpeech();
  // После записи iOS могла оставить разговорный динамик — просим обычный.
  ensurePlaybackSession();
  const decoded = buffer;
  await new Promise<void>((resolve) => {
    const source = context.createBufferSource();
    source.buffer = decoded;
    source.connect(context.destination);
    // Страховка: `ended` может не прийти (контекст прервали) — не висим.
    const timer = window.setTimeout(() => done(), decoded.duration * 1000 + PLAY_SLACK_MS);
    function done() {
      if (finishCurrent !== done) return;
      finishCurrent = null;
      window.clearTimeout(timer);
      source.onended = null;
      try {
        source.stop();
      } catch {
        // уже остановлен
      }
      resolve();
    }
    finishCurrent = done;
    source.onended = () => done();
    source.start();
  });
  return true;
}

export async function playServerSpeech(text: string, rate: number, language: LanguageCode, cancelled: () => boolean = () => false): Promise<void> {
  const speed = Math.min(1.5, Math.max(0.5, Math.round(rate * 20) / 20));
  const url = await audioUrl(text, speed, language);
  // Пока звук грузился, речь остановили (ученик перебил) — не играем.
  if (cancelled()) return;
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  if (await playWithWebAudio(url, cancelled)) return;
  if (cancelled()) return;
  stopServerSpeech();
  const audio = sharedPlayer();
  await new Promise<void>((resolve, reject) => {
    let timer = window.setTimeout(() => done(), PLAY_BASE_MS + text.length * PLAY_MS_PER_CHAR);
    const onMeta = () => {
      if (!Number.isFinite(audio.duration) || audio.duration <= 0) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => done(), audio.duration * 1000 + PLAY_SLACK_MS);
    };
    const onError = () => done(new Error('audio'));
    // Пауза не от нас — входящий звонок, блокировка, Siri: фраза кончилась.
    const onPause = () => {
      if (!audio.ended) done();
    };
    const onEnded = () => done();
    function done(error?: Error) {
      if (finishCurrent !== done) return;
      finishCurrent = null;
      window.clearTimeout(timer);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
      if (error === undefined) resolve();
      else reject(error);
    }
    finishCurrent = done;
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);
    audio.muted = false;
    // После записи iOS могла оставить разговорный динамик — просим обычный.
    ensurePlaybackSession();
    audio.src = url;
    audio.play().catch((error: unknown) => {
      // iOS не пустил без жеста — следующий тап разблокирует плеер заново.
      playerUnlocked = false;
      done(error instanceof Error ? error : new Error('audio'));
    });
  });
}

/* ── Распознавание ───────────────────────────────────────────────── */

/** Громкость (RMS), выше которой в микрофоне — голос, а не фон. */
const VOICE_LEVEL = 0.01;
/**
 * Голос должен звучать хотя бы столько. Щелчок от нажатия на кнопку и
 * шорох короче: раньше они помечали фразу сказанной, и через секунду
 * паузы запись обрывалась до того, как ученик успевал заговорить.
 */
const VOICE_SUSTAIN_MS = 180;
/**
 * Пауза после голоса, после которой фраза считается сказанной. В живом
 * разговоре человек делает паузу ~0,5 с; 1,2 с — это уже «задумался», а
 * для звонка — лишняя секунда ожидания на каждой реплике.
 */
const SILENCE_AFTER_SPEECH_MS = 650;
/** Ученик так и не заговорил — сеанс заканчивается тишиной. */
const NO_SPEECH_MS = 4000;
/** Фраза ученика короче — длинная запись обрывается. */
const MAX_RECORDING_MS = 9000;
/**
 * Анализатор молчит (все отсчёты — ровно нули) дольше этого — значит,
 * браузер приостановил AudioContext, а не ученик молчит. Контекст общий и
 * будится жестом (см. `resumeAudioContext`), поэтому проверка короткая:
 * если звук всё же пропал, лучше записать вслепую, чем ждать.
 */
const DEAD_ANALYSER_MS = 1200;
/** Сколько писать вслепую, если детектор голоса не работает: фраза успевает прозвучать. */
const BLIND_WINDOW_MS = 4500;

/** Форматы записи: webm/opus у Chromium и Firefox, mp4 у Safari, ogg/aac — запасные. */
const RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus', 'audio/aac'] as const;

/** Формат, который браузер действительно умеет писать; `undefined` — отдаём выбор ему. */
function recordingType(): string | undefined {
  if (typeof MediaRecorder.isTypeSupported !== 'function') return undefined;
  return RECORDING_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

function createRecorder(media: MediaStream): MediaRecorder {
  const type = recordingType();
  if (type === undefined) return new MediaRecorder(media);
  try {
    return new MediaRecorder(media, { mimeType: type });
  } catch {
    // Формат заявлен, но конструктор его не принял — пусть браузер выберет сам.
    return new MediaRecorder(media);
  }
}

export interface ServerListenHandlers {
  readonly onFinal: (text: string) => void;
  readonly onSilence: (reason: string) => void;
  /** Запись идёт и голос слышен — для индикатора «слушаю». */
  readonly onInterim?: (text: string) => void;
}

/**
 * Записать одну фразу и распознать на сервере. Причины тишины — как у
 * браузерного распознавания: `no-speech` и `network` (можно снова),
 * `not-allowed` и `audio-capture` (экран переходит на клавиатуру).
 */
export function listenOnServer(handlers: ServerListenHandlers): { readonly stop: () => void } {
  let finished = false;
  /** Аренда общего потока: возвращается в `releaseAudio`. */
  let lease: MicLease | null = null;
  let recorder: MediaRecorder | null = null;
  // Контекст общий и уже разбужен жестом; будим ещё раз на случай, если
  // iOS подвесил его между фразами.
  const context = sharedAudioContext();
  if (context !== null) void context.resume().catch(() => undefined);
  // Узлы детектора текущей записи: отключаем их, но сам контекст живёт —
  // закрытый AudioContext на iOS больше не разбудить без жеста.
  let detector: { readonly source: MediaStreamAudioSourceNode; readonly analyser: AnalyserNode; readonly sink: GainNode } | null = null;
  let frame = 0;
  const timers: number[] = [];
  let cancelled = false;
  /** Голос услышан (или детектор сломан) — запись уйдёт на распознавание. */
  let heard = false;
  /** Детектор молчит: пишем окно фиксированной длины и всё равно отправляем запись. */
  let blind = false;

  /** Снять таймеры и кадры: запись больше не развивается. */
  const clearWatch = () => {
    window.cancelAnimationFrame(frame);
    timers.forEach((timer) => window.clearTimeout(timer));
  };
  /**
   * Отпустить микрофон и узлы детектора. На iOS остановка дорожек посреди
   * отправки обрывает сам POST — сервер видит «request aborted», — поэтому
   * зовём это только когда запись уже ушла и ответ пришёл.
   */
  const releaseAudio = () => {
    // Поток общий: возвращаем, а не останавливаем — следующее касание без
    // нового запроса разрешения (и iOS не обрывает отправку записи).
    lease?.release();
    lease = null;
    // Микрофон не нужен — возвращаем звук в динамик (iOS оставлял talk-режим).
    ensurePlaybackSession();
    if (detector !== null) {
      detector.source.disconnect();
      detector.analyser.disconnect();
      detector.sink.disconnect();
      detector = null;
    }
  };
  const cleanup = () => {
    clearWatch();
    releaseAudio();
  };
  const fail = (reason: string) => {
    if (finished) return;
    finished = true;
    // Запись не завелась вовсе — фраза уйдёт браузерному распознаванию,
    // а сервер минуту не трогаем: иначе каждый тап снова ждал бы отказа.
    if (reason === 'audio-capture') sttDownUntil = Date.now() + STT_BACKOFF_MS;
    console.warn(`[stt] recording for Whisper failed: ${reason}`, { audioContext: audioContextState(), recorder: recorder?.state ?? 'none' });
    cleanup();
    if (recorder?.state === 'recording') recorder.stop();
    handlers.onSilence(reason);
  };

  /**
   * Отправить запись на распознавание: одна повторная попытка. На iOS первый
   * POST иногда обрывается, пока жива аудиосессия записи (свёрнутое окно,
   * переключение приложения); перед повтором отпускаем микрофон и контекст,
   * и та же запись уходит ещё раз уже из «спокойного» состояния. Срок общий
   * на обе попытки, поэтому «…» на экране не висит бесконечно.
   */
  const uploadRecording = async (blob: Blob, type: string): Promise<string> => {
    const deadline = timeoutSignal(TRANSCRIBE_TIMEOUT_MS);
    const send = async (): Promise<string> => {
      const response = await apiFetch('/api/speech/transcribe', {
        method: 'POST',
        body: blob,
        headers: { 'Content-Type': type, 'X-Speech-Source': speechSourceTag },
        signal: deadline,
      });
      if (!response.ok) throw new Error(`speech ${response.status}`);
      const payload = (await response.json()) as { readonly data?: { readonly text?: string } };
      return payload.data?.text?.trim() ?? '';
    };
    try {
      return await send();
    } catch (error: unknown) {
      // Срок вышел или повторить нечем — пусть экран покажет «не расслышала».
      if (deadline.aborted) throw error;
      releaseAudio();
      await new Promise((resolve) => window.setTimeout(resolve, RETRY_DELAY_MS));
      return send();
    }
  };

  /**
   * Детектор голоса не отвечает (на iOS в PWA AudioContext часто остаётся
   * suspended): не выбрасываем запись как «тишину», а пишем вслепую —
   * фраза целиком уйдёт на распознавание.
   */
  const recordBlind = () => {
    if (blind || finished) return;
    blind = true;
    heard = true;
    window.cancelAnimationFrame(frame);
    handlers.onInterim?.('…');
    timers.push(window.setTimeout(() => recorder?.state === 'recording' && recorder.stop(), BLIND_WINDOW_MS));
  };

  // Общий поток: открытый в касании или уже открытый — иначе новый запрос.
  void acquireMic('record')
    .then((taken) => {
      if (cancelled) {
        taken.release();
        return;
      }
      lease = taken;
      const media = taken.stream;
      // Запись пошла — разрешение действительно выдано. На Safari Permissions
      // API молчит, и без этой отметки перебивание оставалось выключенным.
      micAccess = 'granted';
      const chunks: Blob[] = [];
      recorder = createRecorder(media);
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data);
      };
      // Сбой записи (кодек, нехватка памяти) не должен оставлять сеанс
      // в «слушаю» навсегда: сообщаем причину, `speech.ts` попробует
      // браузерное распознавание, если оно есть.
      recorder.onerror = () => fail('audio-capture');
      recorder.onstop = () => {
        if (finished) return;
        finished = true;
        // Микрофон отпускаем не сразу: на iOS закрытая аудиосессия обрывает
        // ещё не завершившийся POST. Если первый POST всё же оборвался,
        // `uploadRecording` отпустит звук сам и повторит отправку.
        clearWatch();
        if (!heard || chunks.length === 0) {
          releaseAudio();
          handlers.onSilence('no-speech');
          return;
        }
        // Параметры кодека (`;codecs=…`) серверу не нужны, ему важен контейнер.
        const type = (recorder?.mimeType || chunks[0]?.type || 'audio/webm').split(';')[0] ?? 'audio/webm';
        void uploadRecording(new Blob(chunks, { type }), type)
          .then((text) => {
            sttDownUntil = 0;
            if (text.length > 0) handlers.onFinal(text);
            else handlers.onSilence('no-speech');
          })
          .catch(() => {
            // Ответа нет или сервер отказал: повторный тап снова попробует
            // сервер, а браузерное распознавание (если есть) — запасной путь.
            handlers.onSilence('network');
          })
          .finally(releaseAudio);
      };

      try {
        // Кусок записи каждые полсекунды: часть мобильных браузеров отдаёт
        // данные только по таймслайсу, а не в конце записи.
        recorder.start(500);
      } catch {
        try {
          // Таймслайс не принят (старый Safari) — пишем одним куском.
          recorder.start();
        } catch {
          fail('audio-capture');
          return;
        }
      }

      const audio = context;
      if (audio === null) {
        recordBlind();
      } else {
        try {
          // Контекст мог заснуть, пока ждали микрофон: будим перед подключением.
          if (audio.state === 'suspended') void audio.resume().catch(() => undefined);
          // Детектор голоса: громкость каждого кадра; голос — начало, пауза — конец.
          const analyser = audio.createAnalyser();
          analyser.fftSize = 1024;
          const source = audio.createMediaStreamSource(media);
          // Граф обязан доходить до выхода — иначе часть браузеров его не
          // запускает; громкость на выходе нулевая, чтобы не было эха.
          const sink = audio.createGain();
          sink.gain.value = 0;
          source.connect(analyser);
          analyser.connect(sink);
          sink.connect(audio.destination);
          detector = { source, analyser, sink };
          const samples = new Float32Array(analyser.fftSize);
          let lastVoice = 0;
          let voiceSince = 0;
          let frames = 0;
          let signal = false;
          const tick = () => {
            if (blind || finished) return;
            analyser.getFloatTimeDomainData(samples);
            let sum = 0;
            for (const sample of samples) {
              sum += sample * sample;
              if (sample !== 0) signal = true;
            }
            frames += 1;
            const level = Math.sqrt(sum / samples.length);
            const now = performance.now();
            if (level > VOICE_LEVEL) {
              if (voiceSince === 0) voiceSince = now;
              // Щелчок от нажатия короче порога — фразой не считается.
              if (heard || now - voiceSince >= VOICE_SUSTAIN_MS) {
                if (!heard) handlers.onInterim?.('…');
                heard = true;
                lastVoice = now;
              }
            } else {
              voiceSince = 0;
            }
            if (heard && now - lastVoice > SILENCE_AFTER_SPEECH_MS) {
              if (recorder?.state === 'recording') recorder.stop();
              return;
            }
            frame = window.requestAnimationFrame(tick);
          };
          frame = window.requestAnimationFrame(tick);
          // Кадры не пришли или все нулевые — контекст приостановлен или
          // кадры не рисуются: переходим к записи вслепую.
          timers.push(window.setTimeout(() => !heard && (frames === 0 || !signal) && recordBlind(), DEAD_ANALYSER_MS));
        } catch {
          recordBlind();
        }
      }

      timers.push(window.setTimeout(() => !heard && fail('no-speech'), NO_SPEECH_MS));
      timers.push(window.setTimeout(() => recorder?.state === 'recording' && recorder.stop(), MAX_RECORDING_MS));
    })
    .catch((error: unknown) => {
      // Причина уже записана в `acquireMic`.
      const name = error instanceof DOMException ? error.name : '';
      fail(name === 'NotAllowedError' || name === 'SecurityError' ? 'not-allowed' : 'audio-capture');
    });

  return {
    stop: () => {
      cancelled = true;
      if (finished) return;
      finished = true;
      cleanup();
      if (recorder?.state === 'recording') {
        recorder.onstop = null;
        recorder.stop();
      }
    },
  };
}

/* ── Перебивание ─────────────────────────────────────────────────── */

/** Громкость, с которой ученик точно говорит сам, а не эхо Тико из динамика. */
const BARGE_IN_LEVEL = 0.045;
const BARGE_IN_SUSTAIN_MS = 280;

/**
 * Пока Тико говорит, слушать микрофон (с эхоподавлением) и сообщить, когда
 * ученик заговорил сам. Разрешение не спрашивается: без уже выданного
 * доступа перебивание просто не работает. Возвращает «прекратить слушать».
 */
export function watchForVoice(onVoice: () => void): () => void {
  let stopped = false;
  const context = sharedAudioContext();
  let source: MediaStreamAudioSourceNode | null = null;
  let analyser: AnalyserNode | null = null;
  let frame = 0;
  let lease: MicLease | null = null;
  const stop = () => {
    stopped = true;
    window.cancelAnimationFrame(frame);
    // Тот же общий поток, что у записи, — возвращаем, а не останавливаем.
    lease?.release();
    lease = null;
    source?.disconnect();
    analyser?.disconnect();
  };
  // iOS: открытый микрофон переключает аудиосессию — голос Тико глохнет,
  // а анализатор вне жеста молчит. Там перебивают касанием по Тико.
  if (isIOS() || micAccess !== 'granted' || !recordingSupported()) return stop;
  if (context === null) return stop;
  // Тот же общий поток — второго запроса разрешения во время звонка нет.
  void acquireMic('barge-in')
    .then((taken) => {
      lease = taken;
      const media = taken.stream;
      if (stopped) {
        stop();
        return;
      }
      void context.resume().catch(() => undefined);
      const node = context.createAnalyser();
      node.fftSize = 1024;
      analyser = node;
      source = context.createMediaStreamSource(media);
      source.connect(node);
      const samples = new Float32Array(node.fftSize);
      let loudSince = 0;
      const tick = () => {
        if (stopped) return;
        node.getFloatTimeDomainData(samples);
        let sum = 0;
        for (const sample of samples) sum += sample * sample;
        const level = Math.sqrt(sum / samples.length);
        const now = performance.now();
        if (level > BARGE_IN_LEVEL) {
          if (loudSince === 0) loudSince = now;
          if (now - loudSince >= BARGE_IN_SUSTAIN_MS) {
            stop();
            onVoice();
            return;
          }
        } else loudSince = 0;
        frame = window.requestAnimationFrame(tick);
      };
      frame = window.requestAnimationFrame(tick);
    })
    .catch(() => undefined);
  return stop;
}
